import React, { useState, useEffect } from 'react';
import { Chart as ChartJS, ArcElement, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Filler, Tooltip } from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { sans, mono, green, greenLight, greenDark, red, redLight, amber, amberLight, useTheme, useCurrency } from '../../tokens';
import { fmtCompactC } from '../../helpers';
import { useTranslation } from '../../i18n';
import { useCompany } from '../../CompanyContext';
import { headcountHistory, departureHistory, departmentList } from '../../data/headcountHistory';
import { getCanonicalHeadcount, assertHeadcountConsistent } from '../../data/canonicalHeadcount';
import { getPublishedSnapshot, publishSnapshot, unpublishSnapshot, buildSnapshotPayload } from '../../data/investorSnapshots';
import VersionHistory from './VersionHistory';
import PortfolioOverview from './PortfolioOverview';
import {
  getSectionLayout, hideSection, showSection, moveSection,
  resolveOrder, subscribeToSectionLayoutChanges,
} from '../../data/sectionLayoutStore';
import { SECTION_REGISTRY } from './SideNav';
import { MONTHLY_ACTUALS } from '../../data/monthlyActuals';
import SideNav from './SideNav';
import CollapsibleSection from './CollapsibleSection';
import AlertsFeed from './AlertsFeed';
import KPIHero from './KPIHero';
import Sparkline from './Sparkline';
import KPISmallMultiples from './KPISmallMultiples';
import OwnershipCard from './OwnershipCard';
import IPBreakdown from './IPBreakdown';
import ExportMenu from './ExportMenu';
import MonthSelector from './MonthSelector';
import LoadingState from './LoadingState';
import useMediaQuery from '../../hooks/useMediaQuery';
import RisksCard from './RisksCard';
import InvestorReporting, { generateNewsletter } from '../InvestorReporting';
import { CurrencyToggle } from '../../App';
import NextRaiseSection from './NextRaiseSection';
import CommercialPipeline from './CommercialPipeline';
import DocumentVault from './DocumentVault';
import ProductRoadmap from './ProductRoadmap';

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Filler, Tooltip);

// ─── Design constants ─────────────────────────────────────────────────────────
const GAP = 16;
const CARD_PAD = '20px 24px';

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Inner card for sub-sections (Capital Structure, Burn Rate, etc.)
function Section({ title, subtitle, children, span }) {
  const t = useTheme();
  return (
    <div style={{
      background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 10,
      padding: CARD_PAD, gridColumn: span ? `span ${span}` : undefined,
    }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: t.textMuted, fontFamily: sans }}>
          {title}
        </div>
        {subtitle && <div style={{ fontSize: 10, color: t.textFaint, fontFamily: sans, marginTop: 2 }}>{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function InvestorKpi({ label, value, sub, color }) {
  const t = useTheme();
  return (
    <div style={{ padding: '18px 20px' }}>
      <div style={{ marginBottom: 6 }}>
        <span style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5, color: t.textMuted, fontWeight: 600, fontFamily: sans }}>{label}</span>
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, fontFamily: mono, color: color || t.textPrimary }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: t.textMuted, marginTop: 3, fontFamily: sans }}>{sub}</div>}
    </div>
  );
}

function TRow({ header, cells }) {
  const t = useTheme();
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: header ? '4px 0' : '5px 0',
      borderBottom: `1px solid ${t.borderLight}`,
    }}>
      {cells.map((c, i) => (
        <div key={i} style={{
          flex: c.width ? undefined : 1, width: c.width,
          fontSize: header ? 9 : 11,
          fontFamily: header ? sans : (c.mono ? mono : sans),
          fontWeight: header ? 600 : 400,
          textTransform: header ? 'uppercase' : undefined,
          letterSpacing: header ? 0.4 : undefined,
          color: c.color || (header ? t.textMuted : t.textPrimary),
          textAlign: c.align || 'left',
        }}>
          {c.value}
        </div>
      ))}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function InvestorPage({ sbData }) {
  const t = useTheme();
  const cc = useCurrency();
  const tr = useTranslation();
  const { activeProfile, activeCompany } = useCompany();
  const isInvestorRole = ['investor', 'board'].includes(activeProfile?.role);
  const isPreview = window.location.search.includes('preview=investor');
  const isReadOnly = isInvestorRole || isPreview;
  const companyId = activeCompany?.id || 'default';
  // Published snapshot is per-period. We re-read whenever the selected period
  // changes so the button reflects that period's current publish state.
  const [publishedSnapshot, setPublishedSnapshot] = useState(null);
  const [publishFlash, setPublishFlash] = useState(null);

  // ─── Data from Supabase ───────────────────────────────────────────────────────
  const sb = sbData?.data;
  const m = sb?.metrics || {};

  const liq = m.liquidity || {};
  const totalLiquidity = liq.total || 35290000;

  const sbBurn = m.burn_data || {};
  // Monthly burn from GL monthly actuals (cash basis)
  const glMonthlyBurn = (() => {
    const months = Object.keys(MONTHLY_ACTUALS['Salaries & Payroll Taxes'] || {}).sort();
    const last6 = months.slice(-6);
    if (last6.length === 0) return 1176000;
    let totalOut = 0, totalIn = 0;
    for (const mo of last6) {
      for (const [cat, data] of Object.entries(MONTHLY_ACTUALS)) {
        const amt = data[mo] || 0;
        if (cat.startsWith('Interest') || cat.startsWith('Grants')) {
          totalIn += Math.abs(amt);
        } else {
          totalOut += amt;
        }
      }
    }
    return Math.round((totalOut - totalIn) / last6.length);
  })();
  const burnValues = sbBurn.burn_6mo || [375000, 668000, 1016000, 2640000, 1910000, 179000];
  const monthlyBurn = sbBurn.burn_6mo ? Math.round(burnValues.reduce((s, v) => s + v, 0) / 6) : glMonthlyBurn;
  const runwayMonths = Math.round(totalLiquidity / monthlyBurn);

  // Original raise currencies from Direction of Payment documents
  const RAISE_CURRENCIES = {
    'Seed': 'CAD',
    'Series A': 'USD',
    'Series A.2': 'USD',
  };
  const fxRate = cc.fxRate || 1.3718;
  const displayCurrency = cc.currency || 'CAD';

  const capitalRaised = (sb?.capitalRaises || []).map(c => {
    const origCurrency = RAISE_CURRENCIES[c.series_name] || 'CAD';
    const origAmount = Number(c.amount);
    const cadAmount = origCurrency === 'USD' ? origAmount * fxRate : origAmount;
    return { series: c.series_name, amount: cadAmount, origAmount, origCurrency };
  });
  const totalRaised = capitalRaised.reduce((s, c) => s + c.amount, 0);
  const deployed = totalRaised - totalLiquidity;

  // Canonical headcount — single source of truth
  const canonicalHC = getCanonicalHeadcount();
  assertHeadcountConsistent(canonicalHC.total, 'InvestorPage');
  const totalHC = canonicalHC.total;
  // R&D headcount: sum Engineering, R&D, Design, Mechanical, Electrical departments
  const rdHC = Object.entries(canonicalHC.departments || {})
    .filter(([name]) => /R&D|Engineering|Design|Mechanical|Electrical|Software/.test(name))
    .reduce((s, [, n]) => s + n, 0);

  const milestones = m.milestones || [];
  const patents = m.patents || { filed: 52, granted: 9, pending: 43, countries: 10 };

  // Burn data: use the same hardcoded values as My Overview/Dashboard for consistency
  // These come from the bank statements (cash-basis burn per month)
  const FALLBACK_BURN = [504000, 933000, 868000, 3080000, 1095000, 1316000, -355000, 1052000, 375000, 668000, 1016000, 2640000, 895000, 800000];
  const FALLBACK_LABELS = ["Jan '25", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan '26", "Feb"];
  const FALLBACK_MONTHS = ['2025-01','2025-02','2025-03','2025-04','2025-05','2025-06','2025-07','2025-08','2025-09','2025-10','2025-11','2025-12','2026-01','2026-02'];
  const burnActual = sbBurn.values || FALLBACK_BURN;
  const burnLabels = sbBurn.labels || FALLBACK_LABELS;
  const glMonths = FALLBACK_MONTHS;

  // Dynamic projection: from current month forward (3 months)
  const projMonthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const projNow = new Date();
  const projLabels = Array.from({ length: 3 }, (_, i) => {
    const mi = (projNow.getMonth() + i) % 12;
    const yr = projNow.getFullYear() + Math.floor((projNow.getMonth() + i) / 12);
    return `${projMonthNames[mi]}${yr !== 2026 ? " '" + String(yr).slice(2) : ''}*`;
  });
  const allBurnLabels = [...burnLabels, ...projLabels];
  const burnProjected = [...Array(burnActual.length).fill(null), ...projLabels.map(() => monthlyBurn)];
  const burnActualPadded = [...burnActual, ...projLabels.map(() => null)];

  const seriesColors = ['#93A3D1', '#A7B5D9', '#BCC7E2', '#D0D9EB', '#E4EBF4'];

  // ─── Historical snapshots (compute EOM balances from burn working backwards) ─
  const burnMonthKeys = glMonths;
  const eomBalances = {};
  eomBalances['2026-02'] = totalLiquidity; // Feb 2026 = latest known
  for (let i = burnActual.length - 1; i >= 0; i--) {
    const prevMonth = i > 0 ? burnMonthKeys[i - 1] : null;
    if (prevMonth) {
      // balance at end of prev month = balance at end of this month + burn this month
      eomBalances[prevMonth] = (eomBalances[burnMonthKeys[i]] || totalLiquidity) + burnActual[i];
    }
  }

  // Map period keys to month keys
  const periodToMonth = { 'jan-2026': '2026-01', 'dec-2025': '2025-12', 'nov-2025': '2025-11' };

  // Compute period-specific KPIs
  const getSnapshotKPIs = (period) => {
    const monthKey = periodToMonth[period];
    if (!monthKey || period === 'jan-2026') {
      // Latest data — use live computed values
      return { liq: totalLiquidity, burn: monthlyBurn, rwy: runwayMonths, hc: totalHC, rdHc: rdHC };
    }
    const monthIdx = burnMonthKeys.indexOf(monthKey);
    const snapLiq = eomBalances[monthKey] || totalLiquidity;
    // Trailing 6-month burn average up to this month
    const trailingBurns = burnActual.slice(Math.max(0, monthIdx - 5), monthIdx + 1);
    const snapBurn = trailingBurns.length > 0 ? Math.round(trailingBurns.reduce((s, v) => s + v, 0) / trailingBurns.length) : monthlyBurn;
    const snapRwy = snapBurn > 0 ? Math.round(snapLiq / snapBurn) : 0;
    // Headcount from headcountHistory
    const hcEntry = headcountHistory.find(h => h.month === monthKey);
    const snapHC = hcEntry ? hcEntry.total : totalHC;
    return { liq: snapLiq, burn: snapBurn, rwy: snapRwy, hc: snapHC, rdHc: rdHC };
  };

  // ─── State ──────────────────────────────────────────────────────────────────
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [hoveredBar, setHoveredBar] = useState(null);
  const [expandedBar, setExpandedBar] = useState(null);
  const [burnPeriod, setBurnPeriod] = useState('L12');
  const [hcDept, setHcDept] = useState('all');
  const [hcView, setHcView] = useState('total');
  const [roadmapView, setRoadmapView] = useState('milestones');
  const [selectedPeriod, setSelectedPeriod] = useState('jan-2026');
  const [financialsForceOpen, setFinancialsForceOpen] = useState(false);

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
  };

  // ─── Per-period section layout ───────────────────────────────────────────
  const [sectionLayout, setSectionLayout] = useState(() => getSectionLayout(companyId, selectedPeriod));
  useEffect(() => setSectionLayout(getSectionLayout(companyId, selectedPeriod)), [companyId, selectedPeriod]);
  useEffect(() => subscribeToSectionLayoutChanges(() => {
    setSectionLayout(getSectionLayout(companyId, selectedPeriod));
  }), [companyId, selectedPeriod]);

  const ALL_SECTION_IDS = SECTION_REGISTRY.map(s => s.id);
  const visibleSectionIds = resolveOrder(sectionLayout, ALL_SECTION_IDS);
  const isVisible = (id) => visibleSectionIds.includes(id);
  const hiddenSections = ALL_SECTION_IDS.filter(id => sectionLayout.hidden.includes(id));
  const onHide   = (id) => { if (!isReadOnly) hideSection(companyId, selectedPeriod, id); };
  const onShow   = (id) => { if (!isReadOnly) showSection(companyId, selectedPeriod, id); };
  const onUp     = (id) => { if (!isReadOnly) moveSection(companyId, selectedPeriod, ALL_SECTION_IDS, id, -1); };
  const onDown   = (id) => { if (!isReadOnly) moveSection(companyId, selectedPeriod, ALL_SECTION_IDS, id, 1); };
  const sectionAdminProps = (id) => {
    // Always pass editable/companyId/selectedPeriod so text-override state
    // stays hydrated even for read-only viewers (they still need to see
    // the override, just without the pencil button).
    const base = { editable: !isReadOnly, companyId, selectedPeriod };
    if (isReadOnly) return base;
    const idx = visibleSectionIds.indexOf(id);
    return {
      ...base,
      onHide: () => onHide(id),
      onMoveUp: () => onUp(id),
      onMoveDown: () => onDown(id),
      canMoveUp: idx > 0,
      canMoveDown: idx >= 0 && idx < visibleSectionIds.length - 1,
    };
  };

  // Re-read the published snapshot for the currently selected period.
  useEffect(() => {
    setPublishedSnapshot(getPublishedSnapshot(companyId, selectedPeriod));
  }, [companyId, selectedPeriod]);

  // In read-only (investor/board/preview) mode, if the currently selected
  // period has no published snapshot, auto-switch to the most recently
  // published period so investors land on something they can actually see.
  useEffect(() => {
    if (!isReadOnly) return;
    if (getPublishedSnapshot(companyId, selectedPeriod)) return;
    // Scan all known periods and pick the one with the newest publishedAt.
    const AVAIL = ['jan-2026', 'dec-2025', 'nov-2025'];
    let best = null;
    for (const p of AVAIL) {
      const snap = getPublishedSnapshot(companyId, p);
      if (snap && (!best || snap.publishedAt > best.snap.publishedAt)) {
        best = { period: p, snap };
      }
    }
    if (best) setSelectedPeriod(best.period);
  }, [isReadOnly, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const isMobile = useMediaQuery('(max-width: 768px)');

  // ─── Portfolio Overview ─────────────────────────────────────────────────
  // Investors with access to multiple companies land on a portfolio grid.
  // Current access control: triggered by ?portfolio=1 for demo purposes;
  // when Supabase multi-tenancy lands, flip to isInvestorRole && companies.length > 1.
  const wantsPortfolio = new URLSearchParams(window.location.search).get('portfolio') === '1';
  const [viewingCompany, setViewingCompany] = useState(null);
  if (wantsPortfolio && !viewingCompany) {
    return (
      <PortfolioOverview
        onOpenCompany={(c) => {
          // Current company stays on the existing single-company flow;
          // other mock companies would route to their own report once
          // multi-tenant is wired in.
          setViewingCompany(c);
        }}
      />
    );
  }

  // ─── Render ─────────────────────────────────────────────────────────────────
  // Read-only viewers (investors/board/preview) see "Data available soon"
  // when the currently selected period has no published snapshot. No live
  // data leaks to investors — they only see what an admin explicitly published.
  if (isReadOnly && !publishedSnapshot) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{
          maxWidth: 440, textAlign: 'center',
          background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 12,
          padding: '40px 32px',
        }}>
          <img src="/finkloz-light.png" alt="" aria-hidden="true" style={{ height: 36, opacity: 0.9, marginBottom: 20 }} />
          <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, fontFamily: sans, marginBottom: 6 }}>
            Data available soon
          </div>
          <div style={{ fontSize: 12, color: t.textMuted, fontFamily: sans, lineHeight: 1.5 }}>
            {activeCompany?.name || 'Demo Medical Devices Inc.'} has not published an investor report yet.
            You'll see it here as soon as it's ready.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {!isMobile && isReadOnly && <SideNav companyId={companyId} selectedPeriod={selectedPeriod} />}
      <div style={{ marginLeft: (isReadOnly && !isMobile) ? 150 : 0, padding: '0 24px 40px' }}>

        {/* Page header */}
        <div id="inv-overview" style={{ padding: '20px 0 12px', borderBottom: '1px solid ' + t.border, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, fontFamily: sans, color: t.textPrimary }}>Investor Dashboard</div>
              <div style={{ fontSize: 11, color: t.textMuted, fontFamily: sans, marginTop: 2 }}>Demo Medical Devices Inc. -- Confidential</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {cc.setCurrency && (
                <CurrencyToggle
                  currency={cc.currency}
                  onToggle={cc.setCurrency}
                  fxRate={cc.fxRate}
                  fxDate={cc.fxDate}
                />
              )}
              <MonthSelector selectedPeriod={selectedPeriod} onPeriodChange={handlePeriodChange} investorOnly={isReadOnly} companyId={companyId} />
              {!isReadOnly && (() => {
                const MONTH_NAMES = { jan: 'January', feb: 'February', mar: 'March', apr: 'April', may: 'May', jun: 'June', jul: 'July', aug: 'August', sep: 'September', oct: 'October', nov: 'November', dec: 'December' };
                const [mo, yr] = (selectedPeriod || '').split('-');
                const prettyPeriod = MONTH_NAMES[mo] && yr ? `${MONTH_NAMES[mo]} ${yr}` : selectedPeriod;
                const isPublished = !!publishedSnapshot;
                return (
                  <button
                    onClick={() => {
                      if (isPublished) {
                        const { dropped, current } = unpublishSnapshot(companyId, selectedPeriod);
                        setPublishedSnapshot(current);
                        const prev = current
                          ? `rolled back to ${new Date(current.publishedAt).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })}`
                          : 'no prior version for this period';
                        setPublishFlash(`${prettyPeriod} unpublished · ${prev}`);
                      } else {
                        const payload = buildSnapshotPayload({
                          liquidity: totalLiquidity,
                          burn: monthlyBurn,
                          runwayMonths,
                          totalHC,
                          period: selectedPeriod,
                          baseCurrency: 'CAD', // GL source is CAD
                          fxRate: cc.fxRate || 1,
                        });
                        const snap = publishSnapshot(companyId, selectedPeriod, activeProfile?.email, payload);
                        setPublishedSnapshot(snap);
                        setPublishFlash(`${prettyPeriod} published at ${new Date(snap.publishedAt).toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit' })}`);
                      }
                      setTimeout(() => setPublishFlash(null), 4000);
                    }}
                    title={isPublished ? `Unpublish ${prettyPeriod} report` : `Publish ${prettyPeriod} report to investors`}
                    style={{
                      padding: '6px 14px', borderRadius: 6,
                      border: `1px solid ${isPublished ? t.border : green}`,
                      background: isPublished ? 'transparent' : green,
                      color: isPublished ? t.textSecondary : '#fff',
                      fontFamily: sans, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {isPublished ? 'Unpublish report' : 'Publish report'}
                  </button>
                );
              })()}
              {!isReadOnly && <ExportMenu onExportPDF={() => generateNewsletter({ currency: cc.currency, fxRate: cc.fxRate })} />}
            </div>
          </div>
          <div style={{ fontSize: 9, fontFamily: mono, color: t.textFaint, marginTop: 4 }}>
            Last updated: {new Date().toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
          {isReadOnly && publishedSnapshot && (() => {
            // Resolve a human-readable publisher label:
            // prefer full_name from the active profile; fall back to email
            // stored on the snapshot.
            const PUBLISHER_TITLES = {
              'taylor.brooks@example.com': 'Taylor Brooks, CPA, Finance Team Lead',
            };
            const publisherLabel = PUBLISHER_TITLES[publishedSnapshot.publishedBy]
              || activeProfile?.full_name
              || publishedSnapshot.publishedBy
              || '';
            return (
              <div style={{
                marginTop: 8, padding: '6px 12px', borderRadius: 6,
                background: greenLight, border: `1px solid ${green}`,
                fontSize: 10, fontFamily: sans, color: greenDark,
                display: 'inline-block',
              }}>
                Report as of {new Date(publishedSnapshot.publishedAt).toLocaleDateString('en-CA', { month: 'long', day: 'numeric', year: 'numeric' })}
                {publisherLabel ? ` — published by ${publisherLabel}` : ''}
              </div>
            );
          })()}
          {isReadOnly && !publishedSnapshot && (
            <div style={{
              marginTop: 8, padding: '6px 12px', borderRadius: 6,
              background: amberLight, border: `1px solid ${amber}`,
              fontSize: 10, fontFamily: sans, color: amber,
              display: 'inline-block',
            }}>
              No report has been published yet — admins must publish before investors see data.
            </div>
          )}
          {!isReadOnly && publishFlash && (
            <div style={{
              marginTop: 8, padding: '6px 12px', borderRadius: 6,
              background: greenLight, border: `1px solid ${green}`,
              fontSize: 10, fontFamily: sans, color: greenDark,
              display: 'inline-block',
            }}>
              {publishFlash}
            </div>
          )}
        </div>

        {/* KPI Hero */}
        {(() => {
          const snap = getSnapshotKPIs(selectedPeriod);
          const periodLabel = selectedPeriod === 'jan-2026' ? '' : ` (${selectedPeriod.split('-').map((p,i) => i === 0 ? p.charAt(0).toUpperCase() + p.slice(1) : p).join(' ')})`;
          return (
            <KPIHero
              liquidity={fmtCompactC(snap.liq, cc)}
              runway={(() => {
                const d = new Date();
                d.setMonth(d.getMonth() + snap.rwy);
                const q = Math.ceil((d.getMonth() + 1) / 3);
                return `Q${q} ${d.getFullYear()}`;
              })()}
              runwayMonths={snap.rwy}
              burn={fmtCompactC(snap.burn, cc)}
              capitalRaised={fmtCompactC(totalRaised, cc)}
              capitalSub={`${capitalRaised.length} rounds`}
              headcount={String(snap.hc)}
              hcSub={`${snap.rdHc} R&D`}
              sparkData={{
                liquidity: burnActual.slice(-6).map((_, i) => totalLiquidity + burnActual.slice(-(6-i)).reduce((s,v) => s+v, 0)),
                burn: burnActual.slice(-6),
                headcount: headcountHistory.slice(-6).map(h => h.total),
                runway: burnActual.slice(-6).map(b => b > 0 ? Math.round(totalLiquidity / b) : runwayMonths),
              }}
            />
          );
        })()}

        {/* Alerts */}
        {isVisible('inv-alerts') && (
        <div id="inv-alerts" style={{ marginTop: 20 }}>
          <CollapsibleSection id="alerts" title="Recent Updates" defaultOpen={true} lastRefreshed={new Date()} {...sectionAdminProps('inv-alerts')}>
            <AlertsFeed readOnly={isReadOnly} selectedPeriod={selectedPeriod} companyId={companyId} />
          </CollapsibleSection>
        </div>)}

        {/* Metrics Over Time */}
        {isVisible('inv-metrics') && (
        <div id="inv-metrics">
          <CollapsibleSection id="metrics" title="Financial Overview" defaultOpen={true} lastRefreshed={new Date()} {...sectionAdminProps('inv-metrics')}>
            <KPISmallMultiples monthlyActuals={MONTHLY_ACTUALS} totalLiquidity={totalLiquidity} burnActual={burnActual} />
          </CollapsibleSection>
        </div>)}

        {/* Capital Structure + Ownership */}
        {isVisible('inv-capital') && (
        <div id="inv-capital">
          <CollapsibleSection id="capital" title="Capital Structure & Ownership" defaultOpen={true} {...sectionAdminProps('inv-capital')}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: GAP }}>
              {/* Capital Structure */}
              <Section title={tr('inv.capitalStructure')} subtitle={tr('inv.capitalStructureSub')}>
                {(() => {
                  let remaining = deployed;
                  const seriesDeployment = capitalRaised.map(c => {
                    const spent = Math.min(remaining, c.amount);
                    remaining = Math.max(0, remaining - c.amount);
                    return { ...c, spent, pct: c.amount > 0 ? Math.round((spent / c.amount) * 100) : 0 };
                  });

                  const sel = selectedSeries !== null ? seriesDeployment[selectedSeries] : null;
                  const viewTotal = sel ? sel.amount : totalRaised;
                  const viewSpent = sel ? sel.spent : deployed;
                  const viewRemaining = sel ? (sel.amount - sel.spent) : totalLiquidity;
                  const viewPct = viewTotal > 0 ? Math.min(Math.round((viewSpent / viewTotal) * 100), 100) : 0;
                  const viewLabel = sel ? sel.series : 'All Rounds';
                  const barColor = sel ? seriesColors[selectedSeries] : '#93A3D1';

                  return (
                    <>
                      {/* Remaining capital display */}
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 8, textTransform: 'uppercase', letterSpacing: 0.4, color: t.textMuted, fontFamily: sans, marginBottom: 2 }}>Remaining</div>
                        <div style={{ fontSize: 18, fontWeight: 700, fontFamily: mono, color: green }}>{fmtCompactC(viewRemaining, cc)}</div>
                      </div>

                      {/* Funding usage bar */}
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                          <span style={{ fontSize: 10, fontFamily: mono, color: t.textSecondary }}>
                            {viewLabel}: {fmtCompactC(viewSpent, cc)} / {fmtCompactC(viewTotal, cc)}
                          </span>
                          <span style={{ fontSize: 11, fontWeight: 600, fontFamily: mono, color: viewPct >= 100 ? red : t.textPrimary }}>{viewPct}% deployed</span>
                        </div>
                        <div style={{ height: 16, borderRadius: 4, overflow: 'hidden', background: t.borderLight, display: 'flex' }}>
                          {selectedSeries === null ? seriesDeployment.map((c, i) => (
                            <div key={i} style={{
                              height: '100%', width: `${(c.spent / totalRaised * 100)}%`,
                              background: seriesColors[i],
                              transition: 'width 0.3s',
                            }} />
                          )) : (
                            <div style={{
                              height: '100%', width: `${viewPct}%`,
                              background: barColor,
                              borderRadius: 4, transition: 'width 0.3s',
                            }} />
                          )}
                        </div>
                      </div>

                      {/* Series breakdown */}
                      <div style={{ borderTop: `1px solid ${t.borderLight}`, paddingTop: 6 }}>
                        {seriesDeployment.map((c, i) => {
                          const isSelected = selectedSeries === i;
                          return (
                            <div key={i}
                              onClick={() => setSelectedSeries(isSelected ? null : i)}
                              style={{
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                padding: '4px 6px', margin: '1px 0', borderRadius: 4, cursor: 'pointer',
                                background: isSelected ? (seriesColors[i] + '18') : 'transparent',
                                transition: 'background 0.15s',
                              }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <div style={{ width: 7, height: 7, borderRadius: 2, background: seriesColors[i], flexShrink: 0 }} />
                                <span style={{ fontSize: 11, fontFamily: sans, color: isSelected ? t.textPrimary : t.textSecondary, fontWeight: isSelected ? 600 : 400 }}>{c.series}</span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                {isSelected && <span style={{ fontSize: 9, fontFamily: mono, color: c.pct >= 100 ? red : t.textMuted }}>{c.pct}% used</span>}
                                <span style={{ fontSize: 11, fontFamily: mono, color: t.textSecondary }}>{fmtCompactC(c.amount, cc)}</span>
                                <span style={{ fontSize: 8, fontFamily: mono, color: t.textFaint, marginLeft: 4 }}>
                                  {['2020', 'Feb 2023', 'Jan 2025'][i] || ''}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}
              </Section>

              {/* Burn Rate */}
              <Section title={tr('inv.cashDeployment')} subtitle={`${fmtCompactC(monthlyBurn, cc)}/mo avg burn -- ${runwayMonths} months runway${selectedSeries !== null ? ` | Viewing: ${capitalRaised[selectedSeries]?.series}` : ''}`}>
                {(() => {
                  const BURN_COLOR = '#7C9CBF';
                  const BALANCE_COLOR = green;

                  const fullEomBals = [];
                  let bal = totalLiquidity;
                  fullEomBals[burnActual.length - 1] = bal;
                  for (let i = burnActual.length - 2; i >= 0; i--) {
                    bal += burnActual[i + 1];
                    fullEomBals[i] = bal;
                  }

                  const nProj = projLabels.length;
                  let sliceStart = 0;
                  if (burnPeriod === 'L12') {
                    sliceStart = Math.max(0, burnActual.length - 12);
                  } else if (burnPeriod === 'YTD') {
                    const curYr = String(new Date().getFullYear()).slice(2);
                    sliceStart = burnLabels.findIndex(l => l.includes("'" + curYr));
                    if (sliceStart < 0) sliceStart = 0;
                  }
                  const visLabels = burnLabels.slice(sliceStart);
                  const visBurn = burnActual.slice(sliceStart);
                  const visBals = fullEomBals.slice(sliceStart);

                  const rollingAvg = visBurn.map((_, i) => {
                    const windowStart = Math.max(0, sliceStart + i - 5);
                    const windowEnd = sliceStart + i + 1;
                    const window = burnActual.slice(windowStart, windowEnd).filter(v => v > 0);
                    return window.length > 0 ? Math.round(window.reduce((s, v) => s + v, 0) / window.length) : null;
                  });

                  const allLabels = [...visLabels, ...projLabels];
                  const lastBal = visBals[visBals.length - 1] || totalLiquidity;
                  const projBals = Array.from({ length: nProj }, (_, i) => lastBal - monthlyBurn * (i + 1));

                  const burnActualPad = [...visBurn, ...Array(nProj).fill(null)];
                  const burnProjPad = [...visBurn.map(() => null), ...Array(nProj).fill(monthlyBurn)];
                  const balData = [...visBals, ...projBals];
                  const avgPad = [...rollingAvg, ...Array(nProj).fill(monthlyBurn)];
                  const nActual = visBurn.length;

                  return (
                    <>
                      {/* Period toggle */}
                      <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
                        {[
                          { key: 'L12', label: tr('inv.last12mo') },
                          { key: 'YTD', label: tr('inv.ytd') },
                          { key: 'ALL', label: tr('inv.all') },
                        ].map(p => (
                          <button key={p.key} onClick={() => setBurnPeriod(p.key)} style={{
                            padding: '3px 10px', borderRadius: 12, border: `1px solid ${burnPeriod === p.key ? green : t.border}`,
                            background: burnPeriod === p.key ? greenLight : 'transparent',
                            color: burnPeriod === p.key ? green : t.textMuted,
                            fontSize: 9, fontWeight: 600, fontFamily: sans, cursor: 'pointer',
                          }}>{p.label}</button>
                        ))}
                      </div>
                      <div style={{ height: 220 }}>
                        <Bar data={{
                          labels: allLabels,
                          datasets: [
                            {
                              label: 'Monthly Burn',
                              data: burnActualPad,
                              backgroundColor: visBurn.map(v => v < 0 ? BALANCE_COLOR + '60' : BURN_COLOR + '50').concat(Array(nProj).fill('transparent')),
                              borderRadius: 3,
                              yAxisID: 'y',
                            },
                            {
                              label: 'Projected Burn',
                              data: burnProjPad,
                              backgroundColor: BURN_COLOR + '40',
                              borderColor: BURN_COLOR + '60',
                              borderWidth: 1,
                              borderDash: [3, 3],
                              borderRadius: 3,
                              yAxisID: 'y',
                            },
                            {
                              label: 'Cash Balance (EOM)',
                              data: balData,
                              type: 'line',
                              borderColor: BALANCE_COLOR,
                              backgroundColor: BALANCE_COLOR + '08',
                              fill: true,
                              tension: 0.3,
                              pointRadius: [...visBals.map(() => 3), ...projBals.map(() => 3)],
                              pointBackgroundColor: [...visBals.map(() => BALANCE_COLOR), ...projBals.map(() => BALANCE_COLOR + '60')],
                              pointBorderColor: [...visBals.map(() => BALANCE_COLOR), ...projBals.map(() => BALANCE_COLOR)],
                              borderWidth: 2,
                              spanGaps: true,
                              segment: {
                                borderDash: ctx => ctx.p0DataIndex >= nActual - 1 ? [4, 4] : undefined,
                              },
                              yAxisID: 'y1',
                            },
                            {
                              label: '6-mo Avg',
                              data: avgPad,
                              type: 'line',
                              borderColor: BURN_COLOR + '50',
                              borderWidth: 1.5,
                              borderDash: [4, 4],
                              pointRadius: 0,
                              fill: false,
                              tension: 0.3,
                              yAxisID: 'y',
                            },
                          ],
                        }} plugins={selectedSeries !== null ? [{
                          id: 'seriesHighlight',
                          beforeDraw: (chart) => {
                            const seriesRanges = { 0: [0, 5], 1: [5, 11], 2: [11, 14] };
                            const range = seriesRanges[selectedSeries];
                            if (!range) return;
                            const { ctx, chartArea, scales } = chart;
                            const xStart = scales.x.getPixelForValue(Math.max(range[0] - sliceStart, 0));
                            const xEnd = scales.x.getPixelForValue(Math.min(range[1] - sliceStart, allLabels.length - 1));
                            if (xEnd <= chartArea.left || xStart >= chartArea.right) return;
                            ctx.save();
                            ctx.fillStyle = (seriesColors[selectedSeries] || '#93A3D1') + '12';
                            ctx.fillRect(Math.max(xStart, chartArea.left), chartArea.top, Math.min(xEnd, chartArea.right) - Math.max(xStart, chartArea.left), chartArea.bottom - chartArea.top);
                            ctx.restore();
                          },
                        }] : []} options={{
                          responsive: true, maintainAspectRatio: false,
                          interaction: { mode: 'index', intersect: false },
                          plugins: {
                            legend: { display: false },
                            tooltip: {
                              callbacks: {
                                label: (ctx) => {
                                  if (ctx.parsed.y == null) return null;
                                  if (ctx.parsed.y < 0) return `${ctx.dataset.label}: ${fmtCompactC(Math.abs(ctx.parsed.y), cc)} (net cash inflow)`;
                                  return `${ctx.dataset.label}: ${fmtCompactC(Math.abs(ctx.parsed.y), cc)}`;
                                },
                              },
                            },
                          },
                          scales: {
                            y: {
                              position: 'left',
                              ticks: { callback: v => fmtCompactC(v, cc), color: t.textMuted, font: { size: 8, family: mono } },
                              grid: { color: t.borderLight },
                              title: { display: true, text: tr('inv.monthlyBurn'), color: t.textFaint, font: { size: 8, family: sans } },
                            },
                            y1: {
                              position: 'right',
                              ticks: { callback: v => fmtCompactC(v, cc), color: BALANCE_COLOR, font: { size: 8, family: mono } },
                              grid: { display: false },
                              title: { display: true, text: 'Cash Balance', color: BALANCE_COLOR, font: { size: 8, family: sans } },
                            },
                            x: {
                              ticks: { color: t.textMuted, font: { size: 8, family: sans }, maxRotation: 0 },
                              grid: { display: false },
                            },
                          },
                        }} />
                      </div>
                      <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 9, color: t.textMuted, fontFamily: sans }}>
                        <span><span style={{ display: 'inline-block', width: 10, height: 6, background: BURN_COLOR + '50', borderRadius: 1, marginRight: 4, verticalAlign: 'middle' }} />{tr('inv.actual')}</span>
                        <span><span style={{ display: 'inline-block', width: 10, height: 6, background: BURN_COLOR + '20', border: `1px dashed ${BURN_COLOR}60`, borderRadius: 1, marginRight: 4, verticalAlign: 'middle' }} />{tr('inv.projected')}</span>
                        <span><span style={{ display: 'inline-block', width: 10, height: 2, background: BALANCE_COLOR, marginRight: 4, verticalAlign: 'middle' }} />{tr('inv.cashBalance')}</span>
                        <span><span style={{ display: 'inline-block', width: 10, height: 0, borderTop: `1.5px dashed ${BURN_COLOR}50`, marginRight: 4, verticalAlign: 'middle' }} />{tr('inv.avgLabel')}</span>
                      </div>
                    </>
                  );
                })()}
              </Section>
            </div>
          </CollapsibleSection>
        </div>)}

        {/* Ownership + SPCC */}
        <div id="inv-ownership">
          <CollapsibleSection id="ownership" title="Ownership & SPCC Status" defaultOpen={true}>
            <OwnershipCard />
          </CollapsibleSection>
        </div>

        {/* Roadmap */}
        {isVisible('inv-roadmap') && (
        <div id="inv-roadmap">
          <CollapsibleSection id="roadmap" title="Product Roadmap" defaultOpen={true} lastRefreshed={new Date()} {...sectionAdminProps('inv-roadmap')}>
            <ProductRoadmap companyId={companyId} readOnly={isReadOnly} selectedPeriod={selectedPeriod} />
          </CollapsibleSection>
        </div>)}

        {/* Risks */}
        {isVisible('inv-risks') && (
        <div id="inv-risks">
          <CollapsibleSection id="risks" title="Risks & Mitigations" defaultOpen={false} lastRefreshed={new Date()} {...sectionAdminProps('inv-risks')}>
            <RisksCard companyId={companyId} readOnly={isReadOnly} />
          </CollapsibleSection>
        </div>)}

        {/* Monthly Highlights */}
        {isVisible('inv-financials') && (
        <div id="inv-financials">
          <CollapsibleSection id="financials" title="Monthly Highlights" defaultOpen={false} summaryWhenClosed="Key highlights, R&D, G&A, team" forceOpen={financialsForceOpen} lastRefreshed={new Date()} {...sectionAdminProps('inv-financials')}>
            <InvestorReporting selectedPeriod={selectedPeriod} companyId={companyId} readOnly={isReadOnly} />
          </CollapsibleSection>
        </div>)}

        {/* Documents */}
        {isVisible('inv-documents') && (
        <div id="inv-documents">
          <CollapsibleSection id="documents" title="Document Vault" defaultOpen={false} lastRefreshed={new Date()} {...sectionAdminProps('inv-documents')}>
            <DocumentVault companyId={companyId} readOnly={isReadOnly} />
          </CollapsibleSection>
        </div>)}

        {/* Version history — admin only */}
        {!isReadOnly && (
          <div style={{ marginTop: 20 }}>
            <VersionHistory
              companyId={companyId}
              selectedPeriod={selectedPeriod}
              currentUser={activeProfile?.email}
              onRestored={(snap) => { setPublishedSnapshot(snap); setPublishFlash(`Restored ${selectedPeriod}`); setTimeout(() => setPublishFlash(null), 3000); }}
            />
          </div>
        )}

        {/* Hidden sections restore panel — admin only */}
        {!isReadOnly && hiddenSections.length > 0 && (
          <div style={{
            marginTop: 20, padding: '14px 18px',
            background: t.bgCard, border: `1px dashed ${t.border}`, borderRadius: 10,
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: t.textMuted, fontFamily: sans, marginBottom: 8 }}>
              Hidden sections for this month
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {hiddenSections.map(id => {
                const meta = SECTION_REGISTRY.find(s => s.id === id);
                if (!meta) return null;
                return (
                  <button key={id} onClick={() => onShow(id)}
                    title={`Restore ${meta.label}`}
                    style={{
                      padding: '4px 12px', borderRadius: 12,
                      border: `1px solid ${t.border}`, background: 'transparent',
                      color: t.textSecondary, fontFamily: sans, fontSize: 10, fontWeight: 600,
                      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                    }}>
                    + {meta.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{ textAlign: 'center', fontSize: 9, color: t.textFaint, fontFamily: sans, padding: '20px 0' }}>
          Confidential: Do not reproduce or distribute without permission. Demo Medical Devices Inc.
        </div>
      </div>
    </div>
  );
}
